import type {
  Alert,
  AlertRule,
  AlertRuleInput,
  CreateTenantInput,
  Device,
  DeviceKind,
  DeviceStatus,
  EventRecord,
  IntegrationTestResult,
  InviteUserInput,
  Me,
  MspOverview,
  NamedVehicle,
  PlateDetection,
  PlateSighting,
  Reading,
  Role,
  SaveVehiclePlateInput,
  Site,
  Tenant,
  TenantOverview,
  TenantUser,
  UnifiIntegration,
  UpdateDeviceInput,
  UpdateTenantInput,
  UpdateUnifiIntegrationInput,
} from '@iot-dashboard/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface DeviceFilters {
  siteId?: string;
  kind?: DeviceKind;
  status?: DeviceStatus;
  q?: string;
  /** Sensors and cameras share the device record but have their own pages. */
  deviceClass?: 'sensor' | 'camera';
}

export interface ReadingsResult {
  items: Reading[];
  from: string;
  to: string;
}

export interface PlateFilters {
  /** Part of a plate, or of the name Protect knows a vehicle by; compared on letters and digits only. */
  plate?: string;
  deviceId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface PlatesResult {
  items: PlateDetection[];
  /** The same reads grouped into one row per vehicle. */
  vehicles: PlateSighting[];
  /** True when the range holds more reads than the limit let through. */
  more: boolean;
  /**
   * Set when a search ran out of time before the start of the range: reads
   * older than this were only searched for the whole plate or name.
   */
  searchedFrom?: string;
}

/** Everything the UI can ask the backend for. Implemented over HTTP and in-memory (demo). */
export interface ApiClient {
  me(): Promise<Me>;
  mspOverview(): Promise<MspOverview>;
  listTenants(): Promise<Tenant[]>;
  createTenant(input: CreateTenantInput): Promise<Tenant>;
  getTenant(tenantId: string): Promise<Tenant>;
  updateTenant(tenantId: string, patch: UpdateTenantInput): Promise<Tenant>;
  suspendTenant(tenantId: string): Promise<Tenant>;
  /** Permanent. `confirmSlug` must be the tenant's slug, as a guard against deleting the wrong one. */
  deleteTenant(tenantId: string, confirmSlug: string): Promise<void>;
  tenantOverview(tenantId: string): Promise<TenantOverview>;
  listSites(tenantId: string): Promise<Site[]>;
  listDevices(tenantId: string, filters?: DeviceFilters): Promise<Device[]>;
  getDevice(tenantId: string, deviceId: string): Promise<Device>;
  updateDevice(tenantId: string, deviceId: string, patch: UpdateDeviceInput): Promise<Device>;
  listReadings(tenantId: string, deviceId: string, from: string, to: string): Promise<ReadingsResult>;
  /** A still image pulled live from a camera. Throws if the camera is unreachable. */
  getCameraSnapshot(tenantId: string, deviceId: string, highQuality?: boolean): Promise<Blob>;
  /** Licence plates read by this tenant's cameras, newest first. */
  listPlates(tenantId: string, filters?: PlateFilters): Promise<PlatesResult>;
  /** The picture Protect sent with a plate read. Throws 404 when it sent none. */
  getPlateThumbnail(tenantId: string, detectionId: string): Promise<Blob>;
  /** Saves the plate of a vehicle Protect sends by name; every read of the name shows it. */
  saveVehiclePlate(tenantId: string, vehicleKey: string, input: SaveVehiclePlateInput): Promise<NamedVehicle>;
  /** Forgets a saved plate; the vehicle's reads go back to showing only its name. */
  deleteVehiclePlate(tenantId: string, vehicleKey: string): Promise<void>;
  listAlerts(tenantId: string, status: 'open' | 'resolved' | 'all'): Promise<Alert[]>;
  resolveAlert(tenantId: string, alertId: string): Promise<Alert>;
  listRules(tenantId: string): Promise<AlertRule[]>;
  createRule(tenantId: string, input: AlertRuleInput): Promise<AlertRule>;
  updateRule(tenantId: string, ruleId: string, input: Partial<AlertRuleInput>): Promise<AlertRule>;
  deleteRule(tenantId: string, ruleId: string): Promise<void>;
  listEvents(tenantId: string, limit?: number, type?: string): Promise<EventRecord[]>;
  listUsers(tenantId: string): Promise<TenantUser[]>;
  inviteUser(tenantId: string, input: InviteUserInput): Promise<TenantUser>;
  resendInvite(tenantId: string, username: string): Promise<void>;
  updateUserRole(tenantId: string, username: string, role: Exclude<Role, 'msp-admin'>): Promise<TenantUser>;
  removeUser(tenantId: string, username: string): Promise<void>;
  /** Removes the user's authenticator app so they can enrol a new one. */
  resetUserMfa(tenantId: string, username: string): Promise<void>;
  getIntegration(tenantId: string): Promise<UnifiIntegration>;
  updateIntegration(tenantId: string, input: UpdateUnifiIntegrationInput): Promise<UnifiIntegration>;
  deleteIntegration(tenantId: string): Promise<void>;
  testIntegration(tenantId: string, apiKey?: string): Promise<IntegrationTestResult>;
  syncNow(tenantId: string): Promise<{ queued: boolean; requestedAt: string }>;
}

type Query = Record<string, string | number | undefined>;

export function createHttpClient(baseUrl: string, getToken: () => Promise<string | null>): ApiClient {
  const base = baseUrl.replace(/\/+$/, '');

  async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
    const token = await getToken();
    if (!token) throw new ApiError(401, 'Unauthorized', 'Your session has expired. Sign in again.');
    const url = new URL(base + path);
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
    const res = await fetch(url.toString(), {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let data: unknown = undefined;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) {
      const err = (data ?? {}) as { error?: string; message?: string; details?: unknown };
      throw new ApiError(res.status, err.error ?? 'Error', err.message ?? `Request failed (${res.status})`, err.details);
    }
    return data as T;
  }

  async function requestBlob(path: string, query?: Query): Promise<Blob> {
    const token = await getToken();
    if (!token) throw new ApiError(401, 'Unauthorized', 'Your session has expired. Sign in again.');
    const url = new URL(base + path);
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
    const res = await fetch(url.toString(), { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let err: { error?: string; message?: string } = {};
      try {
        err = JSON.parse(text) as typeof err;
      } catch {
        // a non-JSON error body: fall through to the generic message
      }
      throw new ApiError(
        res.status,
        err.error ?? 'Error',
        err.message ?? `Request failed (${res.status})`,
      );
    }
    return res.blob();
  }

  const items = async <T>(p: Promise<{ items: T[] }>): Promise<T[]> => (await p).items;
  const t = (tenantId: string) => `/tenants/${encodeURIComponent(tenantId)}`;

  return {
    me: () => request('GET', '/me'),
    mspOverview: () => request('GET', '/msp/overview'),
    listTenants: () => items(request('GET', '/tenants')),
    createTenant: (input) => request('POST', '/tenants', input),
    getTenant: (id) => request('GET', t(id)),
    updateTenant: (id, patch) => request('PATCH', t(id), patch),
    suspendTenant: (id) => request('DELETE', t(id)),
    deleteTenant: (id, confirmSlug) => request('DELETE', t(id), undefined, { permanent: 'true', confirm: confirmSlug }),
    tenantOverview: (id) => request('GET', `${t(id)}/overview`),
    listSites: (id) => items(request('GET', `${t(id)}/sites`)),
    listDevices: (id, f) => items(request('GET', `${t(id)}/devices`, undefined, { ...f })),
    getDevice: (id, d) => request('GET', `${t(id)}/devices/${encodeURIComponent(d)}`),
    updateDevice: (id, d, patch) => request('PATCH', `${t(id)}/devices/${encodeURIComponent(d)}`, patch),
    listReadings: (id, d, from, to) =>
      request('GET', `${t(id)}/devices/${encodeURIComponent(d)}/readings`, undefined, { from, to }),
    listPlates: (id, f) => request('GET', `${t(id)}/plates`, undefined, { ...f }),
    getPlateThumbnail: (id, detectionId) =>
      requestBlob(`${t(id)}/plates/${encodeURIComponent(detectionId)}/thumbnail`),
    saveVehiclePlate: (id, key, input) => request('PUT', `${t(id)}/vehicles/${encodeURIComponent(key)}`, input),
    deleteVehiclePlate: (id, key) => request('DELETE', `${t(id)}/vehicles/${encodeURIComponent(key)}`),
    getCameraSnapshot: (id, d, highQuality) =>
      requestBlob(`${t(id)}/devices/${encodeURIComponent(d)}/snapshot`, highQuality ? { quality: 'high' } : undefined),
    listAlerts: (id, status) => items(request('GET', `${t(id)}/alerts`, undefined, { status })),
    resolveAlert: (id, a) => request('POST', `${t(id)}/alerts/${encodeURIComponent(a)}/resolve`, {}),
    listRules: (id) => items(request('GET', `${t(id)}/alert-rules`)),
    createRule: (id, input) => request('POST', `${t(id)}/alert-rules`, input),
    updateRule: (id, r, input) => request('PUT', `${t(id)}/alert-rules/${encodeURIComponent(r)}`, input),
    deleteRule: (id, r) => request('DELETE', `${t(id)}/alert-rules/${encodeURIComponent(r)}`),
    listEvents: (id, limit, type) => items(request('GET', `${t(id)}/events`, undefined, { limit, type })),
    listUsers: (id) => items(request('GET', `${t(id)}/users`)),
    inviteUser: (id, input) => request('POST', `${t(id)}/users`, input),
    resendInvite: (id, u) => request('POST', `${t(id)}/users/${encodeURIComponent(u)}/resend-invite`, {}),
    updateUserRole: (id, u, role) => request('PATCH', `${t(id)}/users/${encodeURIComponent(u)}`, { role }),
    removeUser: (id, u) => request('DELETE', `${t(id)}/users/${encodeURIComponent(u)}`),
    resetUserMfa: (id, u) => request('POST', `${t(id)}/users/${encodeURIComponent(u)}/reset-mfa`, {}),
    getIntegration: (id) => request('GET', `${t(id)}/integrations/unifi`),
    updateIntegration: (id, input) => request('PUT', `${t(id)}/integrations/unifi`, input),
    deleteIntegration: (id) => request('DELETE', `${t(id)}/integrations/unifi`),
    testIntegration: (id, apiKey) => request('POST', `${t(id)}/integrations/unifi/test`, apiKey ? { apiKey } : {}),
    syncNow: (id) => request('POST', `${t(id)}/integrations/unifi/sync`, {}),
  };
}
